import { textIfOk } from './fetch-helpers.ts';

/*
 * Storage for assignment pages (plans/done/unify-git-store-grading.md workstream
 * A3, retiring the old files.ts/github.ts direct-to-GitHub path). Two
 * implementations of one interface:
 *
 * - ServerStorage: the real thing for logged-in users. Everything goes
 *   through bhs-cs's /api/code endpoints (modules/api-routes-code.ts) into
 *   the per-course git-store repo; the browser never talks to GitHub and
 *   needs no token. The server derives the student directory from the
 *   session; a teacher reviewing another student's work passes
 *   ?student=<username> and must not save (the UI blocks it; the server
 *   would accept a teacher save, attributed to the teacher).
 *
 * - InMemoryStorage: anonymous users, family sessions, demos, /m/
 *   previews, and pages outside any course. Edits live in memory, written
 *   through to localStorage so work at least survives a reload in this
 *   browser.
 *
 * Both are constructed by login.ts makeStorage(); entry points only ever
 * see the Storage interface.
 */

export type SaveResult = {
  // Present when a real commit happened.
  commit?: { sha: string; author: { date: string } };
  // True for browser-only saves (InMemoryStorage).
  local?: boolean;
  // True when the content was identical to what's already saved.
  noChanges?: boolean;
  // True when the server couldn't be reached and the work was kept in
  // localStorage instead; ServerStorage retries in the background and
  // calls onBufferFlushed when it finally lands.
  buffered?: boolean;
  // Present when the save was refused because the assignment directory has
  // moved past what this client last saw — a pair partner saved from
  // another machine (pair mode only; see enablePairMode). Nothing was
  // committed or buffered: the caller surfaces the choice (load theirs /
  // save mine anyway).
  conflict?: ConflictInfo;
  // With buffered: the save failed because we're logged out (401), not
  // because the server was unreachable — the fix is logging in, not waiting.
  authLost?: boolean;
};

// latestBy: who made the directory's newest commit (the email-derived
// username, as the commit events carry it; null when underivable) — a pair
// partner is a driver question, this student's own other tab is not.
export type ConflictInfo = {
  latestSha: string | null;
  savedAt: string | null;
  latestBy: string | null;
};

// One file's part of a commit: new contents, or its removal
// (plans/delete-rename-files.md).
type CommitEntry = { file: string; content: string } | { file: string; delete: true };

// The shape populateRevisions/addRevision in assignment-ui.ts render, plus
// who saved it (username; null when the server couldn't derive one).
export type Revision = {
  sha: string;
  username: string | null;
  commit: { author: { date: string } };
};

type Starter = () => string | Promise<string>;

export interface Storage {
  // Does work persist beyond this browser? (False turns off revision
  // dropdowns, answer syncing, etc. in the entry points.)
  readonly saves: boolean;
  // Pages set this to update their save-status display when a background
  // retry lands buffered work (ServerStorage only; never fires elsewhere).
  onBufferFlushed?: ((result: SaveResult) => void) | null;
  // The ?student= username when a teacher is reviewing someone else's work;
  // null normally. Reads target that student; saves must not happen.
  readonly reviewing: string | null;

  load(file: string): Promise<string>;
  loadAtRevision(file: string, sha: string): Promise<string>;
  // The committed starter served at the page path.
  loadFromWeb(file: string): Promise<string>;
  save(file: string, content: string): Promise<SaveResult>;
  // All files in one commit (or one localStorage write per file).
  saveAll(files: { file: string; content: string }[]): Promise<SaveResult>;
  // Keep `files` in this browser synchronously, without attempting a network
  // save. For the paths where an async save can't be trusted to finish: page
  // teardown (a fetch started in beforeunload can be aborted, and the failure
  // handler that would normally buffer the work never runs on a dead page)
  // and the crash-safety write-through while typing (an OOM tab kill fires no
  // unload events at all). ServerStorage keeps them in the retry buffer —
  // flushed by the next save's backlog ride-along, or on the next visit —
  // and never schedules its own flush (a write-through must not turn into an
  // autosave commit). Must not throw: quota failures are swallowed, since
  // mid-teardown there is nowhere left to show them.
  bufferNow(files: { file: string; content: string }[]): void;
  // Whether unsent work sits in the retry buffer (so a page can say it
  // recovered work from a previous visit and is saving it).
  hasBufferedWork(): boolean;
  // Delete/rename a student-created file (plans/delete-rename-files.md).
  // Foreground, never buffered: a failure throws (the caller shows it and
  // the tabs stay as they were); a pair conflict comes back as {conflict}.
  // A rename is one atomic commit carrying the editor's current content.
  deleteFile(file: string): Promise<SaveResult>;
  rename(from: string, to: string, content: string): Promise<SaveResult>;
  // The file names in the assignment directory at a revision (browse mode).
  listFilesAt(sha: string): Promise<string[]>;
  // Browse mode's Restore: one sync commit making the directory exactly
  // `files` — deletions of unlisted files included — always baseSha-guarded
  // so it can't wipe work committed since this client last looked.
  restore(files: { file: string; content: string }[]): Promise<SaveResult>;
  // Make sure the file exists (committing the starter if not) and return its
  // contents. The default starter is whatever is served at the page path;
  // pass `starters` to supply it some other way (Snap! seeds from the live
  // empty project so no project.xml needs checking in).
  ensureStarter(file: string, starter?: Starter): Promise<string>;
  ensureStarters(
    files: string[],
    starters?: Record<string, Starter>,
  ): Promise<Record<string, string>>;
  // Configured starter files plus any the student has added.
  listFiles(): Promise<string[]>;
  revisions(file: string): Promise<Revision[]>;
  // Commits touching anything in this assignment's directory.
  allRevisions(): Promise<Revision[]>;
  // Latest commit for this assignment (speedrun finish records its sha).
  latestCommit(): Promise<{ sha: string; timestamp: number }>;
  // Throw away any localStorage copy so the next load gets the starter.
  resetLocal(file: string): void;
}

/*
 * Where we are: the page path (starter files are served under it) and, for
 * course pages (/c/<courseId>/<dir>/), the pieces the /api/code URLs need.
 */
export const pageLocation = () => {
  const pagePath = window.location.pathname.replace(/^\/+|\/+$/g, '');
  const m = pagePath.match(/^c\/([^/]+)\/(.+)$/);
  return { pagePath, courseId: m?.[1] ?? null, dir: m?.[2] ?? null };
};

/*
 * The ?student= query param: a teacher viewing a specific student's work.
 */
export const reviewStudent = (): string | null =>
  new URL(window.location.href).searchParams.get('student');

const throwWithStatus = async (r: Response): Promise<never> => {
  const err = new Error(`${r.status} ${r.url}`) as Error & { status: number };
  err.status = r.status;
  throw err;
};

// How often buffered (failed) saves are retried.
const FLUSH_RETRY_MS = 20_000;

export class ServerStorage implements Storage {
  readonly saves = true;
  readonly reviewing: string | null;
  // Called when background retry finally lands buffered work, so pages can
  // update their save-status display.
  onBufferFlushed: ((result: SaveResult) => void) | null = null;
  // Called when a background flush hits a pair conflict (pair mode only) —
  // retrying can't resolve it, someone has to choose.
  onConflict: ((conflict: ConflictInfo) => void) | null = null;
  #courseId: string;
  #dir: string;
  #pagePath: string;
  #flushTimer: number | undefined;
  // Pair mode (plans/pair-code-sharing.md): when on, every save carries the
  // latest commit sha this client has seen for the assignment directory
  // (from allRevisions/saves) so a partner's newer save 409s instead of
  // being stomped.
  #pairMode = false;
  #dirSha: string | null = null;
  // The background flush's commit while one is in flight (resolved
  // otherwise), so a page can wait it out the way it waits out its own
  // foreground save — the flush's commit event must not read as someone
  // else moving the directory.
  #flushInFlight: Promise<void> = Promise.resolve();

  constructor(courseId: string, dir: string, pagePath: string, reviewing: string | null) {
    this.#courseId = courseId;
    this.#dir = dir;
    this.#pagePath = pagePath;
    this.reviewing = reviewing;
    // Work buffered in an earlier session (saved while the server was
    // unreachable, then the tab closed) starts retrying immediately.
    if (!reviewing && this.#buffered().length > 0) this.#scheduleFlush(0);
    // The moments the server is most likely back — the browser regaining a
    // network, the tab being looked at again after a deploy blip — retry at
    // once rather than waiting out the interval.
    if (!reviewing) {
      window.addEventListener('online', () => this.#flushSoon());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.#flushSoon();
      });
    }
  }

  #url(base: string, file?: string, params: Record<string, string> = {}) {
    const path = file ? `${this.#dir}/${file}` : this.#dir;
    const q = new URLSearchParams(params);
    if (this.reviewing) q.set('student', this.reviewing);
    const qs = q.size > 0 ? `?${q}` : '';
    return `/api/${base}/${this.#courseId}/${path}${qs}`;
  }

  // ---- The save buffer (plan workstream A step 9) -------------------------
  //
  // A failed save is kept in localStorage and retried in the background (and
  // survives reloads: load() prefers buffered content, which is by
  // construction newer than anything the server has). The buffer clears the
  // moment any save of the same files succeeds.

  #bufferKey(file: string) {
    return `buffer:${this.#pagePath}/${file}`;
  }

  #buffered(): { file: string; content: string }[] {
    const prefix = `buffer:${this.#pagePath}/`;
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(prefix))
      .flatMap((k) => {
        // A key can't vanish between keys() and getItem(), but narrow rather
        // than assert.
        const content = localStorage.getItem(k);
        return content === null ? [] : [{ file: k.slice(prefix.length), content }];
      });
  }

  #clearBuffer(files: { file: string }[]) {
    for (const { file } of files) localStorage.removeItem(this.#bufferKey(file));
  }

  bufferNow(files: { file: string; content: string }[]) {
    if (this.reviewing) return; // never buffer another student's code as ours
    for (const { file, content } of files) {
      try {
        localStorage.setItem(this.#bufferKey(file), content);
      } catch {
        // Quota (Snap-sized projects): the in-flight save, if any, is now
        // the only copy — nothing more we can do from a teardown handler.
      }
    }
  }

  hasBufferedWork() {
    return this.#buffered().length > 0;
  }

  // Drop these files' buffered copies without saving them — for "Load their
  // version" in the pair-conflict flow, where the buffer holds OUR version
  // (kept safe by the conflict stash instead) and a later background flush
  // must not resurrect it over the partner's work we just chose to keep.
  discardBuffered(files: string[]) {
    this.#clearBuffer(files.map((file) => ({ file })));
  }

  // Bring a pending retry forward to now (no-op when nothing is buffered):
  // what the online / tab-visible hooks do, callable by a page (and tests).
  flushNow() {
    this.#flushSoon();
  }

  #flushSoon() {
    if (this.#buffered().length === 0) return;
    if (this.#flushTimer !== undefined) {
      window.clearTimeout(this.#flushTimer);
      this.#flushTimer = undefined;
    }
    this.#scheduleFlush(0);
  }

  #scheduleFlush(delayMs = FLUSH_RETRY_MS) {
    if (this.#flushTimer !== undefined) return;
    this.#flushTimer = window.setTimeout(async () => {
      this.#flushTimer = undefined;
      const buffered = this.#buffered();
      if (buffered.length === 0) return;
      let done: () => void = () => {};
      this.#flushInFlight = new Promise<void>((resolve) => {
        done = resolve;
      });
      try {
        const result = await this.#commitAll(buffered);
        this.#clearBuffer(buffered);
        this.onBufferFlushed?.(result);
      } catch (e) {
        // A pair conflict can't be retried away — keep the buffer (the
        // work is safe) and hand the choice to the page.
        const conflict = (e as { status?: number; conflict?: ConflictInfo }).conflict;
        if ((e as { status?: number }).status === 409 && conflict) {
          this.onConflict?.(conflict);
          return;
        }
        this.#scheduleFlush();
      } finally {
        done();
      }
    }, delayMs);
  }

  // Resolves once no background flush is mid-commit (at once when none is).
  get pendingFlush(): Promise<void> {
    return this.#flushInFlight;
  }

  async load(file: string) {
    // Buffered content is the student's latest work — the server copy is
    // older by construction.
    const buffered = localStorage.getItem(this.#bufferKey(file));
    if (buffered !== null) return buffered;
    const r = await fetch(this.#url('code', file));
    if (!r.ok) return throwWithStatus(r);
    const body = await r.json();
    return body.encoding === 'base64' ? atob(body.content) : body.content;
  }

  async loadAtRevision(file: string, sha: string) {
    const r = await fetch(this.#url('code', file, { ref: sha }));
    if (!r.ok) return throwWithStatus(r);
    const body = await r.json();
    return body.encoding === 'base64' ? atob(body.content) : body.content;
  }

  loadFromWeb(file: string) {
    return fetch(`/${this.#pagePath}/${file}`).then(textIfOk);
  }

  async save(file: string, content: string) {
    return this.saveAll([{ file, content }]);
  }

  // ---- Pair mode ----------------------------------------------------------

  enablePairMode() {
    this.#pairMode = true;
  }

  // The latest commit sha this client has seen for the assignment directory
  // (null = none yet). Updated from allRevisions/latestRevision and real
  // commits; noteDirSha is how the page accepts a newer sha it learned of
  // (after reloading a partner's work, or to deliberately save over it).
  get dirSha(): string | null {
    return this.#dirSha;
  }

  noteDirSha(sha: string | null) {
    this.#dirSha = sha;
  }

  // The single newest commit touching this assignment's directory, or null
  // when there are none — the freshness poll's probe. Does NOT update
  // dirSha: the caller decides when the client has caught up to it. Marked
  // X-Background-Poll so the server's /admin recent-activity box doesn't
  // count the poll's cadence as the user being present.
  async latestRevision(): Promise<Revision | null> {
    const [latest] = await this.#revisions(
      this.#url('code-revisions', undefined, { limit: '1' }),
      false,
      { 'X-Background-Poll': '1' },
    );
    return latest ?? null;
  }

  // ---- Saving --------------------------------------------------------------

  // The raw one-commit save, no buffering — throws on failure (a pair
  // conflict throws with status 409 and the conflict detail attached).
  // sync ("make the directory exactly these files", browse mode's Restore)
  // always carries baseSha — the server refuses it otherwise.
  async #commitAll(files: CommitEntry[], opts: { sync?: boolean } = {}): Promise<SaveResult> {
    const body: Record<string, unknown> = { files };
    if (opts.sync) body.sync = true;
    if (this.#pairMode || opts.sync) body.baseSha = this.#dirSha;
    const r = await fetch(this.#url('code-commit'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (r.status === 409) {
      const detail = await r.json().catch(() => ({}));
      const err = new Error(`409 ${r.url}`) as Error & { status: number; conflict: ConflictInfo };
      err.status = 409;
      err.conflict = {
        latestSha: detail.latestSha ?? null,
        savedAt: detail.savedAt ?? null,
        latestBy: detail.latestBy ?? null,
      };
      throw err;
    }
    if (!r.ok) return throwWithStatus(r);
    const { sha, savedAt, noop } = await r.json();
    if (!noop) this.#dirSha = sha; // a real commit is now the dir's latest
    return noop ? { noChanges: true } : { commit: { sha, author: { date: savedAt } } };
  }

  async saveAll(files: { file: string; content: string }[]) {
    // Anything already buffered rides along so one recovery commit carries
    // the whole backlog (files in this save win over their buffered copies).
    const saving = new Set(files.map((f) => f.file));
    const backlog = this.#buffered().filter((b) => !saving.has(b.file));
    try {
      const result = await this.#commitAll([...files, ...backlog]);
      this.#clearBuffer([...files, ...backlog]);
      return result;
    } catch (e) {
      // A pair conflict is not an outage: nothing to buffer or retry, the
      // caller shows the choice.
      const conflict = (e as { status?: number; conflict?: ConflictInfo }).conflict;
      if ((e as { status?: number }).status === 409 && conflict) {
        return { conflict };
      }
      // Keep the work in this browser and retry in the background. If even
      // localStorage fails (quota — think Snap-sized projects), surface the
      // original save failure.
      try {
        for (const { file, content } of files) {
          localStorage.setItem(this.#bufferKey(file), content);
        }
      } catch {
        throw e;
      }
      console.error('Save failed; work kept in this browser, will retry:', e);
      this.#scheduleFlush();
      // 401: the session is gone (redeploy, expiry). The retry loop still
      // runs — it lands as soon as the student logs back in in any tab —
      // but the page should say "log in", not "server unreachable".
      const authLost = (e as { status?: number }).status === 401;
      return authLost ? { buffered: true, authLost } : { buffered: true };
    }
  }

  async ensureStarter(file: string, starter?: Starter) {
    const contents = await this.ensureStarters([file], starter ? { [file]: starter } : {});
    return contents[file];
  }

  async ensureStarters(files: string[], starters: Record<string, Starter> = {}) {
    const contents: Record<string, string> = {};
    const missing: string[] = [];
    await Promise.all(
      files.map(async (file) => {
        try {
          contents[file] = await this.load(file);
        } catch (e) {
          if ((e as { status?: number }).status !== 404) throw e;
          missing.push(file);
        }
      }),
    );
    if (missing.length > 0) {
      const starterFiles = await Promise.all(
        missing.map(async (file) => {
          const starter = starters[file];
          const content = starter ? await starter() : await this.loadFromWeb(file);
          return { file, content };
        }),
      );
      // When reviewing we must not write into the student's directory; just
      // show the starters for any files they never opened.
      if (!this.reviewing) {
        // One ifAbsent commit for all of them: idempotent, so a second tab
        // racing us is harmless.
        const r = await fetch(this.#url('code-commit', undefined, { ifAbsent: 'true' }), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ files: starterFiles }),
        });
        if (!r.ok) return throwWithStatus(r);
      }
      for (const { file, content } of starterFiles) {
        contents[file] = content;
      }
    }
    return contents;
  }

  // ---- File management (plans/delete-rename-files.md) ----------------------
  //
  // Delete, rename, and Restore are foreground operations, deliberately
  // unbuffered: they either land or fail visibly with the tabs unchanged —
  // buffering a pending deletion would need a whole state machine for a
  // rare case. Each shares #commitAll, so pair mode's baseSha (and its 409
  // → {conflict}) rides along.

  // #commitAll for the foreground operations: a pair conflict comes back as
  // a value, anything else still throws for the caller to report.
  async #commitForeground(files: CommitEntry[], opts: { sync?: boolean } = {}) {
    try {
      return await this.#commitAll(files, opts);
    } catch (e) {
      const conflict = (e as { status?: number; conflict?: ConflictInfo }).conflict;
      if ((e as { status?: number }).status === 409 && conflict) return { conflict };
      throw e;
    }
  }

  async deleteFile(file: string): Promise<SaveResult> {
    const result = await this.#commitForeground([{ file, delete: true }]);
    // An outage-buffered copy must not resurrect the file via the next
    // save's backlog ride-along.
    if (!result.conflict) this.#clearBuffer([{ file }]);
    return result;
  }

  async rename(from: string, to: string, content: string): Promise<SaveResult> {
    const result = await this.#commitForeground([
      { file: to, content },
      { file: from, delete: true },
    ]);
    if (!result.conflict) this.#clearBuffer([{ file: from }, { file: to }]);
    return result;
  }

  async listFilesAt(sha: string): Promise<string[]> {
    const r = await fetch(this.#url('code-files', undefined, { ref: sha }));
    if (!r.ok) return throwWithStatus(r);
    return (await r.json()).files;
  }

  async restore(files: { file: string; content: string }[]): Promise<SaveResult> {
    const result = await this.#commitForeground(files, { sync: true });
    if (!result.conflict) this.#clearBuffer(files);
    return result;
  }

  async listFiles() {
    // The files under this assignment's directory are whatever the latest
    // commits touched; derive them from the directory log.
    const r = await fetch(this.#url('code-files'));
    if (!r.ok) return throwWithStatus(r);
    return (await r.json()).files;
  }

  async revisions(file: string) {
    return this.#revisions(this.#url('code-revisions', file), false);
  }

  async allRevisions() {
    // The dir-level history — its newest sha is the client's baseline for
    // pair-mode conflict checks.
    return this.#revisions(this.#url('code-revisions'), true);
  }

  async #revisions(
    url: string,
    noteDir: boolean,
    headers?: Record<string, string>,
  ): Promise<Revision[]> {
    const r = await fetch(url, headers && { headers });
    if (!r.ok) return throwWithStatus(r);
    const body = await r.json();
    const revisions = body.revisions.map(
      (c: { sha: string; username?: string | null; author: { date: string } }) => ({
        sha: c.sha,
        username: c.username ?? null,
        commit: { author: { date: c.author.date } },
      }),
    );
    if (noteDir) this.#dirSha = revisions[0]?.sha ?? null;
    return revisions;
  }

  async latestCommit() {
    const [latest] = await this.#revisions(
      this.#url('code-revisions', undefined, { limit: '1' }),
      false,
    );
    if (!latest) throw new Error('no commits for this assignment yet');
    return {
      sha: latest.sha,
      timestamp: Math.floor(Date.parse(latest.commit.author.date) / 1000),
    };
  }

  resetLocal(_file: string) {
    // Nothing local to reset.
  }
}

export class InMemoryStorage implements Storage {
  readonly saves = false;
  readonly reviewing = null;
  onBufferFlushed: ((result: SaveResult) => void) | null = null;
  #pagePath: string;
  #inMemory: Record<string, string[]> = {};

  constructor(pagePath: string) {
    this.#pagePath = pagePath;
  }

  #localKey(file: string) {
    return `local:${this.#pagePath}/${file}`;
  }

  async load(file: string) {
    const versions = this.#inMemory[file];
    if (versions?.length) return versions[versions.length - 1];
    const saved = localStorage.getItem(this.#localKey(file));
    return saved !== null ? saved : this.loadFromWeb(file);
  }

  async loadAtRevision(_file: string, _sha: string): Promise<string> {
    throw new Error('no revisions in browser-only storage');
  }

  loadFromWeb(file: string) {
    return fetch(`/${this.#pagePath}/${file}`).then(textIfOk);
  }

  async save(file: string, content: string): Promise<SaveResult> {
    if (!this.#inMemory[file]) {
      this.#inMemory[file] = [];
    }
    this.#inMemory[file].push(content);
    localStorage.setItem(this.#localKey(file), content);
    return { local: true };
  }

  async saveAll(files: { file: string; content: string }[]) {
    for (const { file, content } of files) {
      await this.save(file, content);
    }
    return { local: true };
  }

  bufferNow(files: { file: string; content: string }[]) {
    // localStorage IS this storage's persistence; write it synchronously.
    for (const { file, content } of files) {
      try {
        localStorage.setItem(this.#localKey(file), content);
      } catch {
        // quota — nowhere to show it mid-teardown
      }
    }
  }

  hasBufferedWork() {
    return false; // nothing here ever waits on a server
  }

  async deleteFile(file: string): Promise<SaveResult> {
    delete this.#inMemory[file];
    localStorage.removeItem(this.#localKey(file));
    return { local: true };
  }

  async rename(from: string, to: string, content: string): Promise<SaveResult> {
    delete this.#inMemory[from];
    localStorage.removeItem(this.#localKey(from));
    return this.save(to, content);
  }

  // Browse mode never engages here — saves:false hides the revisions
  // dropdown — so these are unreachable, like loadAtRevision.
  async listFilesAt(_sha: string): Promise<string[]> {
    throw new Error('no revisions in browser-only storage');
  }

  async restore(_files: { file: string; content: string }[]): Promise<SaveResult> {
    throw new Error('no revisions in browser-only storage');
  }

  async ensureStarter(file: string, starter?: Starter) {
    try {
      return await this.load(file);
    } catch {
      if (starter) return starter();
      throw new Error(`no starter for ${file}`);
    }
  }

  async ensureStarters(files: string[], starters: Record<string, Starter> = {}) {
    const contents: Record<string, string> = {};
    await Promise.all(
      files.map(async (file) => {
        contents[file] = await this.ensureStarter(file, starters[file]);
      }),
    );
    return contents;
  }

  async listFiles() {
    const prefix = `local:${this.#pagePath}/`;
    const persisted = Object.keys(localStorage)
      .filter((k) => k.startsWith(prefix))
      .map((k) => k.slice(prefix.length));
    return [...new Set([...Object.keys(this.#inMemory), ...persisted])];
  }

  async revisions(_file: string): Promise<Revision[]> {
    return [];
  }

  async allRevisions(): Promise<Revision[]> {
    return [];
  }

  async latestCommit(): Promise<{ sha: string; timestamp: number }> {
    throw new Error('no commits in browser-only storage');
  }

  resetLocal(file: string) {
    localStorage.removeItem(this.#localKey(file));
  }

  // Throw away every browser-local copy for this page — starter files and
  // student-added ones alike — so the next load starts from the served
  // starters. Callers reload the page afterward; nothing re-fills the
  // editor in place.
  resetAllLocal() {
    const prefix = `local:${this.#pagePath}/`;
    for (const key of Object.keys(localStorage).filter((k) => k.startsWith(prefix))) {
      localStorage.removeItem(key);
    }
    this.#inMemory = {};
  }
}
